import React, { useMemo } from 'react';
import { WheelPickerWrapper } from '@ncdai/react-wheel-picker';
import { WheelPicker } from '../wheel-picker/WheelPicker';
import moment from 'moment';
import { DATE_FORMAT } from 'src/pages/constants';
import styles from './dob-selection.module.scss';

interface DOBSelectionProps {
  value: string | null;
  onChange: (value: string | null) => void;
  minDate: Date;
  maxDate: Date;
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export const DOBSelection: React.FC<DOBSelectionProps> = ({
  value,
  onChange,
  minDate,
  maxDate,
}) => {
  // Parse the current value
  const dateVal = useMemo(() => {
    if (value) {
      const parsed = moment(value);
      if (parsed.isValid()) return parsed;
    }
    return moment(maxDate);
  }, [value, maxDate]);

  const selectedYear = dateVal.year();
  const selectedMonthIndex = dateVal.month(); // 0-11
  const selectedDay = dateVal.date(); // 1-31

  // Generate years list (descending)
  const years = useMemo(() => {
    const minYear = minDate.getFullYear();
    const maxYear = maxDate.getFullYear();
    const list: string[] = [];
    for (let y = maxYear; y >= minYear; y--) {
      list.push(y.toString());
    }
    return list;
  }, [minDate, maxDate]);

  // Generate months list
  const months = MONTHS;

  // Generate days list based on selected month and year
  const days = useMemo(() => {
    const daysInMonth = new Date(
      selectedYear,
      selectedMonthIndex + 1,
      0
    ).getDate();
    return Array.from({ length: daysInMonth }, (_, i) => (i + 1).toString());
  }, [selectedMonthIndex, selectedYear]);

  // Handle changes
  const handleYearChange = (yearStr: string) => {
    const year = parseInt(yearStr, 10);
    const daysInNewDate = new Date(year, selectedMonthIndex + 1, 0).getDate();
    const day = Math.min(selectedDay, daysInNewDate);
    const newDate = moment({ year, month: selectedMonthIndex, day });
    onChange(newDate.format(DATE_FORMAT));
  };

  const handleMonthChange = (monthName: string) => {
    const monthIndex = MONTHS.indexOf(monthName);
    if (monthIndex === -1) return;
    const daysInNewDate = new Date(selectedYear, monthIndex + 1, 0).getDate();
    const day = Math.min(selectedDay, daysInNewDate);
    const newDate = moment({ year: selectedYear, month: monthIndex, day });
    onChange(newDate.format(DATE_FORMAT));
  };

  const handleDayChange = (dayStr: string) => {
    const day = parseInt(dayStr, 10);
    const newDate = moment({
      year: selectedYear,
      month: selectedMonthIndex,
      day,
    });
    onChange(newDate.format(DATE_FORMAT));
  };

  return (
    <div className={styles.container}>
      <div className={styles.headersRow}>
        <span className={`${styles.columnLabel} ${styles.dayLabel}`}>Day</span>
        <span className={`${styles.columnLabel} ${styles.monthLabel}`}>
          Month
        </span>
        <span className={`${styles.columnLabel} ${styles.yearLabel}`}>
          Year
        </span>
      </div>

      <WheelPickerWrapper className={styles.wheelsContainer}>
        <WheelPicker
          className={styles.dayPicker}
          options={days}
          value={selectedDay.toString()}
          onValueChange={handleDayChange}
        />

        <WheelPicker
          className={styles.monthPicker}
          options={months}
          value={MONTHS[selectedMonthIndex]}
          onValueChange={handleMonthChange}
        />

        <WheelPicker
          className={styles.yearPicker}
          options={years}
          value={selectedYear.toString()}
          onValueChange={handleYearChange}
        />
      </WheelPickerWrapper>
    </div>
  );
};

export default DOBSelection;
